import type { ComponentProps, ComponentPropsWithoutRef, ElementType } from 'react';

import { Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import Tooltip from '@/components/Tooltip';
import Typography from '@/components/Typography';

import type { ExtraProps } from 'react-markdown';

export const MarkdownLink: ElementType<ComponentProps<'a'> & ExtraProps> = ({ node, ...props }) => (
  <Tooltip
    title='Click to see index information'
    variant='secondary'
  >
    <Link
      {...props}
      color='textPrimary'
      rel='noopener noreferrer'
      target='_blank'
    />
  </Tooltip>
);

export const MarkdownH1: ElementType<ComponentProps<'h1'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{
      fontSize: '20px',
      margin: '20px 0',
      fontWeight: 700
    }}
    variant='h1'
    {...props}
  />
);

export const MarkdownH2: ElementType<ComponentProps<'h2'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{
      fontSize: '16px',
      margin: '20px 0',
      fontWeight: 700
    }}
    variant='h2'
    {...props}
  />
);

export const MarkdownH3: ElementType<ComponentProps<'h3'> & ExtraProps> = ({ node, ...props }) => (
  <Typography
    sx={{
      fontSize: '14px',
      margin: '20px 0',
      fontWeight: 700
    }}
    variant='h3'
    {...props}
  />
);

export const MarkdownTable: ElementType<ComponentProps<'table'> & ExtraProps> = ({
  node,
  ...props
}) => <Table {...props} />;

export const MarkdownThead: ElementType<ComponentProps<'thead'> & ExtraProps> = ({
  node,
  ...props
}) => <TableHead {...props} />;

export const MarkdownTbody: ElementType<ComponentProps<'tbody'> & ExtraProps> = ({
  node,
  ...props
}) => <TableBody {...props} />;

export const MarkdownTr: ElementType<ComponentProps<'tr'> & ExtraProps> = ({ node, ...props }) => (
  <TableRow {...props} />
);

export const MarkdownTableCell: ElementType<
  (ComponentProps<'th'> | ComponentProps<'td'>) & ExtraProps
> = ({ node, ...props }) => {
  const isHeader = node?.tagName === 'th';
  const mappedProps: ComponentPropsWithoutRef<typeof TableCell> = {
    ...props,
    align: props.align === 'char' ? 'inherit' : props.align
  };
  return (
    <TableCell
      align={isHeader ? 'center' : 'left'}
      sx={{
        border: '0.5px solid white',
        ...(isHeader
          ? {
              fontWeight: 700
            }
          : {})
      }}
      {...mappedProps}
    />
  );
};
